import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  TextField,
  Chip,
  Avatar,
  Typography,
  InputAdornment,
  CircularProgress,
  FormHelperText,
  Alert,
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { LeadDTO } from '../../api/generated/requests/types.gen';
import { LeadSearchService, LeadCrudService } from '../../api/generated/requests/services.gen';
import { useQuery, useQueryClient, useQueries } from '@tanstack/react-query';

interface MultiContactSelectProps {
  value: number[];
  onChange: (value: number[]) => void;
  onBlur?: () => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  onAddNewContact?: () => void;
  parentDialogOpen?: boolean; // New prop to indicate parent dialog is open
}

const MultiContactSelect: React.FC<MultiContactSelectProps> = ({
  value = [],
  onChange,
  onBlur,
  label = "Przypisane kontakty",
  helperText,
  error = false,
  size = 'small',
  fullWidth = true,
  onAddNewContact,
  parentDialogOpen = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [showMinCharWarning, setShowMinCharWarning] = useState(false);
  const queryClient = useQueryClient();

  // Search query - only trigger when searchTerm has 3+ characters
  const shouldSearch = searchTerm.trim().length >= 3;
  
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['lead-contacts-search', searchTerm.trim()],
    queryFn: () => LeadSearchService.getApiLeadContactsSearch({ q: searchTerm.trim() }),
    enabled: shouldSearch && open,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Default paginated query - when no search or search term < 3 chars
  const { data: defaultResults, isLoading: isLoadingDefault } = useQuery({
    queryKey: ['lead-contacts-paginated-default'],
    queryFn: () => LeadCrudService.getApiLeadContactsPaginated({
      page: 0,
      size: 40,
      sortBy: 'CREATED_AT',
      sortDirection: 'DESC'
    }),
    enabled: !shouldSearch && (open || parentDialogOpen),
    staleTime: 60000, // Cache for 1 minute
  });

  // Determine which data to use
  const leads = shouldSearch ? (searchResults || []) : (defaultResults?.content || []);
  const isLoading = shouldSearch ? isSearching : isLoadingDefault;

  // Find lead IDs that are not in current leads array and not in cache
  const missingLeadIds = value.filter(leadId => {
    const leadInResults = leads.find(l => l.id === leadId);
    const cachedLead = queryClient.getQueryData(['LeadCrudServiceGetApiLeadContactsById', { id: leadId }]);
    return !leadInResults && !cachedLead;
  });

  // Fetch missing leads using useQueries
  const missingLeadQueries = useQueries({
    queries: missingLeadIds.map(leadId => ({
      queryKey: ['LeadCrudServiceGetApiLeadContactsById', { id: leadId }],
      queryFn: () => LeadCrudService.getApiLeadContactsById({ id: leadId }),
      staleTime: 300000, // Cache for 5 minutes
      enabled: !!leadId
    }))
  });

  // Helper function to create display name from lead object
  const getDisplayNameFromLead = (lead: LeadDTO) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    }
    return lead.phone || 'Nieznany kontakt';
  };

  // Helper function to get display name by lead ID
  const getLeadDisplayName = (leadId: number) => {
    // First, try to find in current leads array
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      return getDisplayNameFromLead(lead);
    }

    // Try to get the lead from cache using individual query
    const cachedLead = queryClient.getQueryData(['LeadCrudServiceGetApiLeadContactsById', { id: leadId }]);
    if (cachedLead) {
      return getDisplayNameFromLead(cachedLead as LeadDTO);
    }

    // Check if we have fetched data for this lead ID
    const missingLeadIndex = missingLeadIds.indexOf(leadId);
    if (missingLeadIndex !== -1 && missingLeadQueries[missingLeadIndex]?.data) {
      return getDisplayNameFromLead(missingLeadQueries[missingLeadIndex].data as LeadDTO);
    }

    // If we're currently fetching this lead, show loading state
    if (missingLeadIndex !== -1 && missingLeadQueries[missingLeadIndex]?.isLoading) {
      return 'Ładowanie...';
    }

    // Final fallback - this should rarely happen now
    return `ID: ${leadId}`;
  };

  // Handle search term changes
  useEffect(() => {
    if (searchTerm.trim().length > 0 && searchTerm.trim().length < 3) {
      setShowMinCharWarning(true);
    } else {
      setShowMinCharWarning(false);
    }
  }, [searchTerm]);

  // Listen for refresh-leads event to invalidate cache when new contacts are created
  useEffect(() => {
    const handleRefreshLeads = () => {
      // Invalidate both search and default queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['lead-contacts-search'] });
      queryClient.invalidateQueries({ queryKey: ['lead-contacts-paginated-default'] });
    };

    window.addEventListener('refresh-leads', handleRefreshLeads);
    
    return () => {
      window.removeEventListener('refresh-leads', handleRefreshLeads);
    };
  }, [queryClient]);

  const handleOpen = () => {
    setOpen(true);
    setSearchTerm('');
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleToggleContact = (leadId: number) => {
    const newValue = value.includes(leadId)
      ? value.filter(id => id !== leadId)
      : [...value, leadId];
    onChange(newValue);
  };

  const handleRemoveContact = (leadId: number) => {
    const newValue = value.filter(id => id !== leadId);
    onChange(newValue);
  };

  const renderValue = (selected: any) => {
    if (!Array.isArray(selected) || selected.length === 0) {
      return <em style={{ color: '#666', fontSize: '0.8rem' }}>Brak wybranych kontaktów</em>;
    }
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((leadId: number) => (
          <Chip
            key={leadId}
            label={getLeadDisplayName(leadId)}
            onDelete={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleRemoveContact(leadId);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            size="small"
            avatar={
              <Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>
                <BusinessIcon sx={{ fontSize: 12 }} />
              </Avatar>
            }
            sx={{ fontSize: '0.75rem' }}
          />
        ))}
      </Box>
    );
  };

  return (
    <FormControl size={size} fullWidth={fullWidth} error={error}>
      <InputLabel id="multi-contact-select-label">{label}</InputLabel>
      <Select
        labelId="multi-contact-select-label"
        multiple
        value={value}
        label={label}
        onBlur={onBlur}
        onOpen={handleOpen}
        onClose={handleClose}
        open={open}
        renderValue={renderValue}
        startAdornment={
          isLoading ? (
            <InputAdornment position="start">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
              minWidth: 300,
            },
          },
        }}
        // Prevent default onChange behavior since we handle it manually
        onChange={() => {}}
      >
        {/* Search field */}
        <Box sx={{ p: 1, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Szukaj kontaktu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>

        <Divider />

        {/* Minimum character warning */}
        {showMinCharWarning && (
          <>
            <Box sx={{ p: 1 }}>
              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                Wpisz co najmniej 3 znaki aby wyszukać kontakty
              </Alert>
            </Box>
            <Divider />
          </>
        )}

        {/* Add new contact option */}
        {onAddNewContact && (
          <>
            <MenuItem onClick={(e) => { e.stopPropagation(); onAddNewContact(); handleClose(); }}>
              <ListItemIcon>
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main', fontSize: '0.75rem' }}>
                  <AddIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                    Dodaj nowy kontakt
                  </Typography>
                }
              />
            </MenuItem>
            <Divider />
          </>
        )}

        {/* Loading state */}
        {isLoading && (
          <MenuItem disabled>
            <ListItemIcon>
              <CircularProgress size={20} />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography sx={{ fontSize: '0.8rem' }}>
                  Ładowanie kontaktów...
                </Typography>
              }
            />
          </MenuItem>
        )}

        {/* No results */}
        {!isLoading && !showMinCharWarning && leads.length === 0 && (
          <MenuItem disabled>
            <ListItemText 
              primary={
                <Typography sx={{ fontSize: '0.8rem' }} color="text.secondary">
                  {shouldSearch ? `Brak wyników dla "${searchTerm}"` : 'Brak kontaktów'}
                </Typography>
              }
            />
          </MenuItem>
        )}

        {/* Contacts list */}
        {!isLoading && !showMinCharWarning && leads.map((lead) => {
          const isSelected = value.includes(lead.id || 0);
          
          return (
            <MenuItem 
              key={lead.id} 
              onClick={() => lead.id && handleToggleContact(lead.id)}
              dense
            >
              <ListItemIcon>
                <Checkbox
                  checked={isSelected}
                  size="small"
                />
              </ListItemIcon>
              <ListItemIcon>
                <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main', fontSize: '0.75rem' }}>
                  <BusinessIcon sx={{ fontSize: 12 }} />
                </Avatar>
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {getDisplayNameFromLead(lead)}
                  </Typography>
                }
              />
            </MenuItem>
          );
        })}
      </Select>
      
      {helperText && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default MultiContactSelect;